"use client";

import { useState } from "react";
import Link from "next/link";
import { SafeImage } from "@/components/ui/safe-image";
import type { Product } from "@/types";
import type { RecentProduct } from "@/store/recently-viewed.store";
import { formatCurrency, discountPercent, mediaUrl } from "@/lib/utils";
import { QuickAddButton } from "@/components/product/quick-add-button";
import { WishlistButton } from "@/components/product/wishlist-button";
import { FlyToCart } from "@/components/product/fly-to-cart";
import { useDealerStatus } from "@/hooks/use-dealer-status";

const BLUR_DATA_URL = "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";

interface ProductCardProps {
  product: Product | RecentProduct;
  index?: number;
}

// Bold kart: hard border + offset block shadow, press-on-hover, rotated price sticker.
export function StickerProductCard({ product, index = 99 }: ProductCardProps) {
  const [isFlying, setIsFlying] = useState(false);
  const [flyStartRect, setFlyStartRect] = useState<DOMRect | undefined>(undefined);
  const discount = discountPercent(product.price, product.compareAtPrice);
  const isOutOfStock = product.trackStock && product.stock <= 0;

  const { isApproved, discountPercentage } = useDealerStatus();
  const dealerPrice = isApproved
    ? (product.dealerPrice ?? (discountPercentage > 0 ? product.price * (1 - discountPercentage / 100) : null))
    : null;

  const handleAddToCart = (btn: HTMLButtonElement) => {
    setFlyStartRect(btn.getBoundingClientRect());
    setIsFlying(true);
  };

  return (
    <>
      <FlyToCart
        isFlying={isFlying}
        startRect={flyStartRect}
        productImageUrl={mediaUrl(product.coverImageUrl)}
        onDone={() => setIsFlying(false)}
      />
      <Link href={`/products/${product.slug}`} className="group block h-full">
        <div className="flex h-full flex-col overflow-hidden border-2 border-[var(--sf-card-border)] bg-[var(--sf-card-bg)] shadow-[5px_5px_0_var(--sf-card-border)] transition-all duration-150 group-hover:translate-x-[3px] group-hover:translate-y-[3px] group-hover:shadow-none">
          <div className="relative aspect-square overflow-hidden border-b-2 border-[var(--sf-card-border)] bg-[var(--sf-bg)]">
            <SafeImage
              src={product.coverImageUrl ? mediaUrl(product.coverImageUrl) : undefined}
              alt={product.name}
              fill
              priority={index < 3}
              placeholder="blur"
              blurDataURL={BLUR_DATA_URL}
              className="object-cover card-image"
              style={{ objectPosition: `${product.coverFocalX ?? 50}% ${product.coverFocalY ?? 50}%` }}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              fallbackClassName="text-gray-300"
            />

            {discount > 0 && (
              <span
                className="absolute right-2 top-2 z-10 rotate-12 border-2 border-[var(--sf-card-title)] bg-[var(--sf-badge-sale-bg)] px-2 py-0.5 text-xs font-bold uppercase text-[var(--sf-badge-sale-text)]"
                style={{ fontFamily: "var(--sf-font-heading)" }}
              >
                -%{discount}
              </span>
            )}

            {isOutOfStock && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <span className="border-2 border-[var(--sf-card-title)] bg-white px-3 py-1 text-xs font-bold uppercase tracking-widest text-[var(--sf-card-title)]">
                  Tükendi
                </span>
              </div>
            )}

            <div className="absolute right-2 top-2 z-10 flex translate-y-1.5 items-center gap-1 opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
              <WishlistButton
                productId={product.id}
                name={product.name}
                slug={product.slug}
                price={product.price}
                coverImageUrl={product.coverImageUrl}
                size="sm"
                className="h-7 w-7"
              />
            </div>

            <QuickAddButton product={product} onAdd={handleAddToCart} />
          </div>

          <div className="flex flex-1 flex-col items-start p-3">
            <h3
              className="mb-2 line-clamp-2 min-h-[2.5rem] text-sm font-bold uppercase tracking-tight text-[var(--sf-card-title)]"
              style={{ fontFamily: "var(--sf-font-heading)" }}
            >
              {product.name}
            </h3>
            <div className="mt-auto flex flex-wrap items-baseline gap-x-2">
              {dealerPrice ? (
                <>
                  <span className="text-xs text-gray-400 line-through">{formatCurrency(product.price)}</span>
                  <span className="text-base font-bold text-[var(--sf-card-price)]">{formatCurrency(dealerPrice)}</span>
                </>
              ) : (
                <span className={`text-base font-bold ${isOutOfStock ? "text-gray-400" : "text-[var(--sf-card-price)]"}`}>
                  {formatCurrency(product.price)}
                </span>
              )}
              {product.compareAtPrice && product.compareAtPrice > product.price && !dealerPrice && (
                <span className="text-xs text-gray-400 line-through">{formatCurrency(product.compareAtPrice)}</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </>
  );
}
